import React from 'react';
import {
	DemurrageTerms,
	EstimateStatus,
	LaytimeIntervals,
	LaytimeTerms,
} from '@shared/utils/constants';
import { getDemurrageTermLabel } from '@shared/utils/demurrageTerm';
import { formatCurrency } from '@shared/utils/currency';
import { capitalize } from '@shared/utils/string';
import { isMissingOrZero } from '@shared/utils/number';
import type { GetCargoDetailsResponse } from '@api/features/cargos/getCargoDetails';
import EmptyText from '@client/components/EmptyText';
import { ModalModes } from '../CargoDetailsScreen';

export const getLaytimeFields = ({
	cargoDetails,
	mode,
	laytimeTerms,
}: {
	mode: ModalModes;
	cargoDetails: GetCargoDetailsResponse;
	laytimeTerms: LaytimeTerms;
}) => {
	const disabled = cargoDetails?.status === EstimateStatus.Fixed && cargoDetails.voyageId == null;
	const creatingCargo = mode === 'creatingOnContract';
	const demurrageWord = getDemurrageTermLabel(cargoDetails?.demurrageTerm);

	const getDemurrageDespatchFields = () => {
		if (cargoDetails == null) {
			return [];
		}

		if (laytimeTerms === LaytimeTerms.NON_REVERSIBLE) {
			return [
				{
					key: 'demurrageDespatchInterval',
					label: `${demurrageWord} / Despatch interval`,
					value: cargoDetails.demurrageDespatchInterval,
					type: 'select',
					editable: !disabled,
					options: Object.values(LaytimeIntervals).map((s: LaytimeIntervals) => ({
						label: capitalize(s),
						value: s,
					})),
				},
			];
		}

		if (laytimeTerms === LaytimeTerms.AVERAGED) {
			return [
				{
					key: 'timeAllowedInterval',
					label: 'Time Allowed interval',
					value: cargoDetails.timeAllowedInterval,
					type: 'select',
					editable: !disabled,
					options: Object.values(LaytimeIntervals).map((s: LaytimeIntervals) => ({
						label: capitalize(s),
						value: s,
					})),
				},
				{
					key: 'divider',
					label: '',
					editable: false,
					value: (
						<div style={{ margin: 5, visibility: 'hidden', border: 'none' }} />
					),
				},
				{
					key: 'timeAllowedLoading',
					label: 'Total time allowed loading',
					value: cargoDetails.timeAllowedLoading,
					type: 'number',
					editable: !disabled,
					inputProps: {
						'data-testid': 'time-allowed-loading',
						suffix: cargoDetails.timeAllowedInterval,
					},
				},
				{
					key: 'estimatedTimeUsedLoading',
					label: 'Est. Time used loading',
					value: cargoDetails.estimatedTimeUsedLoading,
					type: 'number',
					editable: !disabled,
					inputProps: {
						'data-testid': 'time-used-loading',
						suffix: cargoDetails.timeAllowedInterval,
					},
				},
				{
					key: 'divider',
					label: '',
					editable: false,
					value: (
						<div style={{ margin: 5, visibility: 'hidden', border: 'none' }} />
					),
				},
				{
					key: 'timeAllowedDischarging',
					label: 'Total time allowed discharging',
					value: cargoDetails.timeAllowedDischarging,
					type: 'number',
					editable: !disabled,
					inputProps: {
						'data-testid': 'time-allowed-discharging',
						suffix: cargoDetails.timeAllowedInterval,
					},
				},
				{
					key: 'estimatedTimeUsedDischarging',
					label: 'Est. Time used discharging',
					value: cargoDetails.estimatedTimeUsedDischarging,
					type: 'number',
					editable: !disabled,
					inputProps: {
						'data-testid': 'time-used-discharging',
						suffix: cargoDetails.timeAllowedInterval,
					},
				},
				{
					key: 'divider',
					label: '',
					editable: false,
					value: (
						<div style={{ margin: 5, visibility: 'hidden', border: 'none' }} />
					),
				},
				{
					key: 'demurrageDespatchInterval',
					label: `${demurrageWord} / Despatch interval`,
					value: cargoDetails.demurrageDespatchInterval,
					type: 'select',
					editable: !disabled,
					options: Object.values(LaytimeIntervals).map((s: LaytimeIntervals) => ({
						label: capitalize(s),
						value: s,
					})),
				},
				{
					key: 'demurrageLoading',
					label: `Loading ${demurrageWord.toLowerCase()}`,
					value: cargoDetails.demurrageLoading,
					type: 'currency',
					inputProps: {
						currency: cargoDetails.currency,
						suffix: cargoDetails.demurrageDespatchInterval,
						isRequiredHighlight: isMissingOrZero(cargoDetails.demurrageLoading),

					},
					editable: !disabled,
				},
				{
					key: 'despatchLoading',
					label: 'Loading despatch',
					value: cargoDetails.despatchLoading,
					type: 'currency',
					inputProps: {
						currency: cargoDetails.currency,
						suffix: cargoDetails.demurrageDespatchInterval,
						isRequiredHighlight: isMissingOrZero(cargoDetails.despatchLoading),
					},
					editable: !disabled,
				},
				{
					key: 'demurrageDischarge',
					label: `Discharge ${demurrageWord.toLowerCase()}`,
					value: cargoDetails.demurrageDischarge,
					type: 'currency',
					inputProps: {
						currency: cargoDetails.currency,
						suffix: cargoDetails.demurrageDespatchInterval,
						isRequiredHighlight: isMissingOrZero(cargoDetails.demurrageDischarge),
					},
					editable: !disabled,
				},
				{
					key: 'despatchDischarge',
					label: 'Discharge despatch',
					value: cargoDetails.despatchDischarge,
					type: 'currency',
					inputProps: {
						currency: cargoDetails.currency,
						suffix: cargoDetails.demurrageDespatchInterval,
						isRequiredHighlight: isMissingOrZero(cargoDetails.despatchDischarge),
					},
					editable: !disabled,
				},
			];
		}

		const demurrageLabel: string | React.ReactElement = `${demurrageWord} rate`;
		const despatchLabel: string | React.ReactElement = 'Despatch rate';

		return [
			{
				key: 'timeAllowedInterval',
				label: 'Time allowed interval',
				value: cargoDetails.timeAllowedInterval,
				type: 'select',
				editable: !disabled,
				options: Object.values(LaytimeIntervals).map((s: LaytimeIntervals) => ({
					label: capitalize(s),
					value: s,
				})),
			},
			{
				key: 'timeAllowed',
				label: 'Total time allowed',
				value: cargoDetails.timeAllowed,
				type: 'number',
				editable: !disabled,
				inputProps: {
					suffix: cargoDetails.timeAllowedInterval,
				},
			},
			{
				key: 'estimatedTimeUsed',
				label: 'Est. Total time used',
				value: cargoDetails.estimatedTimeUsed,
				type: 'number',
				editable: !disabled,
				inputProps: {
					'data-testid': 'time-used',
					suffix: cargoDetails.timeAllowedInterval,
				},
			},
			{
				key: 'demurrageDespatchInterval',
				label: `${demurrageWord} / Despatch interval`,
				value: cargoDetails.demurrageDespatchInterval,
				type: 'select',
				editable: !disabled,
				options: Object.values(LaytimeIntervals).map((s: LaytimeIntervals) => ({
					label: capitalize(s),
					value: s,
				})),
			},
			{
				key: 'demurrage',
				label: demurrageLabel,
				value: cargoDetails.demurrage,
				type: 'currency',
				inputProps: {
					currency: cargoDetails.currency,
					suffix: cargoDetails.demurrageDespatchInterval,
					isRequiredHighlight: isMissingOrZero(cargoDetails.demurrage),
				},
				editable: !disabled,
				render: () => (
					<>
						{cargoDetails.demurrage != null ?
							`${formatCurrency(cargoDetails.demurrage, cargoDetails.currency)} / day` :
							(<EmptyText />)}
					</>
				),
			},
			{
				key: 'despatch',
				label: despatchLabel,
				value: cargoDetails.despatch,
				type: 'currency',
				inputProps: {
					currency: cargoDetails.currency,
					suffix: cargoDetails.demurrageDespatchInterval,
					isRequiredHighlight: isMissingOrZero(cargoDetails.despatch),
				},
				editable: !disabled,
				render: () => (
					<>
						{cargoDetails.despatch != null ?
							`${formatCurrency(cargoDetails.despatch, cargoDetails.currency)} / day` :
							(<EmptyText />)}
					</>
				),
			},
		];
	};

	return [
		{
			required: creatingCargo,
			key: 'laytimeTerms',
			label: 'Laytime type',
			value: laytimeTerms,
			type: 'select',
			options: Object.values(LaytimeTerms).map((c) => ({
				label: c,
				value: c,
			})),
			editable: !disabled,
			inputProps: {
				placeholder: 'Select Type',
				showSearch: true,
			},
			render: () => (
				<>
					{laytimeTerms != null ? laytimeTerms : (<EmptyText />)}
				</>
			),
		},
		{
			key: 'despatchHalfDemurrage',
			label: `Despatch half ${demurrageWord.toLowerCase()} (DHD)`,
			value: cargoDetails.despatchHalfDemurrage !== false,
			type: 'switch',
			editable: !disabled,
			render: () => (
				cargoDetails.despatchHalfDemurrage !== false ? 'Enabled' : 'Disabled'
			),
		},
		{
			key: 'demurrageTerm',
			label: 'Demurrage / detention',
			value: cargoDetails.demurrageTerm,
			type: 'select',
			editable: !disabled,
			options: Object.values(DemurrageTerms).map((t) => ({
				label: t,
				value: t,
			})),
		},
		...getDemurrageDespatchFields(),
	];
};
